<template>
  <div class="product-card" @click="goToItem()">
    <img
      class="product-image"
      :src="product.images?.length ? product.images[0]?.image : require('@/assets/images/logo_info.svg')"
      :alt="product.name"
      loading="lazy"
      decoding="async"
    />
    <button
      aria-label="Добавить в избранное"
      class="like-button"
      :class="{ liked: likedProduct === true }"
      @click.stop="clickLike"
    >
      <svg width="35" height="30" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.18054 13.5549L7.17983 13.5542C5.11017 11.6365 3.43202 10.0786 2.26545 8.62023C1.10468 7.16915 0.5 5.8759 0.5 4.49591C0.5 2.23655 2.22166 0.5 4.4 0.5C5.6368 0.5 6.83521 1.09095 7.61714 2.02186L8 2.47767L8.38286 2.02186C9.16479 1.09095 10.3632 0.5 11.6 0.5C13.7783 0.5 15.5 2.23655 15.5 4.49591C15.5 5.8759 14.8953 7.16915 13.7346 8.62023C12.568 10.0786 10.8898 11.6365 8.82017 13.5542L8.81946 13.5549L8 14.3171L7.18054 13.5549Z"
          stroke="#313132"
        />
      </svg>
    </button>
    <div class="product-info d-flex">
      <div class="col-10">
        <h3>
          {{
            product.sale_price && Number(product.sale_price) > 0
              ? formatPrice(product.sale_price)
              : formatPrice(product.price)
          }}
        </h3>
        <p>
          <router-link :to="`/marketplace/${product.id}`" @click.stop>{{ product.name }}</router-link>
        </p>
      </div>
      <div class="rating">★ {{ product.rating }}</div>
    </div>
    <div class="mt-auto">
      <button v-if="quantityInCart === 0" class="btn btn-light-custom w-100 transition-button" @click.stop="addToCart">
        <span translate="yes">Добавить в корзину</span>
      </button>

      <!-- Если товар есть — показываем блок "- n +" -->
      <div v-else class="counter-group d-flex align-items-center justify-content-between">
        <button class="counter-btn" @click.stop="updateCount(-1)">–</button>
        <span class="quantity-text">{{ quantityInCart }}</span>
        <button class="counter-btn" @click.stop="updateCount(1)">+</button>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent, ref, computed } from 'vue';
  import MarketplaceService from '@/services/marketplace';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Modal } from 'bootstrap';

  export default defineComponent({
    name: 'ProductCard',
    props: {
      product: {
        type: Object,
        required: true,
      },
    },
    setup(props) {
      const store = useStore();
      const router = useRouter();
      const productAdded = ref(false);
      const likedProduct = ref(props.product.is_liked);
      const isLoggedIn = computed(() => store.state.auth.status.loggedIn);

      const clickLike = async () => {
        if (isLoggedIn.value) {
          if (!likedProduct.value) {
            await MarketplaceService.addToFavorite(props.product.id);
            likedProduct.value = true;
          } else {
            await MarketplaceService.removeFromFavorite(props.product.id);
            likedProduct.value = false;
          }
        } else {
          openModal();
        }
      };

      const quantityInCart = computed(() => {
        const item = store.state.user.cartList?.find((i) => i.product.id === props.product.id);
        return item ? item.quantity : 0;
      });

      const openModal = () => {
        const loginModal = new Modal(document.getElementById('authModal'));
        loginModal.show();
      };

      const addToCart = async () => {
        if (isLoggedIn.value) {
          await updateCount(1);
          store.dispatch('notifications/triggerToast', {
            message: 'Товар добавлен',
            type: 'success',
          });
        } else {
          openModal();
        }
      };

      const goToItem = () => {
        router.push({
          name: 'MarketplaceItem',
          params: { id: props.product.id },
        });
      };

      const formatPrice = (price) => {
        return new Intl.NumberFormat('ru-RU', {
          style: 'currency',
          currency: 'RUB',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(price);
      };

      const updateCount = async (delta) => {
        try {
          await MarketplaceService.addToBasket(props.product.id, delta);
          await store.dispatch('user/getCartItems');
        } catch (e) {
          console.error(e);
        }
      };

      return {
        goToItem,
        clickLike,
        addToCart,
        formatPrice,
        likedProduct,
        productAdded,
        router,
        quantityInCart,
        updateCount,
      };
    },
  });
</script>

<style scoped lang="scss">
  p {
    overflow: hidden;

    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .like-button {
    position: absolute;
    top: 1rem;
    right: 1.5rem;

    padding: 0;

    background-color: inherit;
    border: none;
    cursor: pointer;

    svg {
      transition: all 0.2s;

      fill: #fff;

      path {
        stroke: #3e3e3e;
      }
    }

    &.liked {
      svg {
        fill: #d44233;

        path {
          stroke: #d44233;
        }
      }
    }
  }

  .product-card {
    position: relative;

    &:hover {
      cursor: pointer;
    }
  }

  .product-image {
    width: 100%;
    height: 220px;
    object-fit: cover;

    border-radius: 30px;
  }

  .product-info {
    margin-top: 10px;
  }

  .rating {
    margin-bottom: 10px;

    color: #ffc107;
  }

  .transition-button {
    height: 42px;

    font-weight: 700;

    border-radius: 20px;

    transition: all 0.3s ease;
  }

  .btn-light-custom {
    color: #0066ce;

    background-color: #d5eaff;
    border: none;
  }

  .btn-added {
    color: white !important;

    background-color: #58b251 !important;
    border: none;
  }

  .icon-white {
    filter: brightness(0) invert(1);
  }

  .counter-group {
    height: 42px;
    padding: 4px;

    background-color: #f4f6fa;
    border: 1px solid #e1e1e1;
    border-radius: 20px;

    .counter-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;

      color: #0066ce;
      font-weight: bold;

      background: #fff;
      border: none;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgb(0 0 0 / 5%);

      transition: 0.2s;

      &:hover {
        color: #fff;

        background: #0066ce;
      }
    }

    .quantity-text {
      color: #3e3e3e;
      font-weight: 700;
      font-size: 16px;
    }
  }

  @media (max-width: 768px) {
    .product-image {
      height: 140px; /* Уменьшаем высоту картинки */

      border-radius: 16px;
    }

    .product-info h3 {
      margin-bottom: 2px;

      font-size: 15px !important;
    }

    .product-info p {
      font-size: 13px !important;
    }

    .like-button {
      top: 0.5rem;
      right: 0.5rem;

      svg {
        width: 24px;
        height: 24px;
      }
    }

    .transition-button {
      height: 34px;

      font-size: 10px;

      border-radius: 12px;
    }

    .counter-group {
      height: 34px;
      padding: 2px 6px;

      .counter-btn {
        width: 26px;
        height: 26px;

        font-size: 16px;
      }

      .quantity-text {
        font-size: 14px;
      }
    }
  }
</style>
